import {
	type API_ScheduleSessionWithRelationsResponse,
	CalendarSessionColorStatus,
	DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
	type FE_ScheduleSession,
	type FeatFlagCalendarSessionColorLegend,
	FeatFlagCalendarSessionTitleViewMode,
	RecurrencyType,
} from "@pilatestime/shared-types";

// Default indigo used when a staff member has no assigned color.
export const DEFAULT_SESSION_COLOR = "#6366f1";

/**
 * Resolve the background color for a session, following the single rule shared by
 * every attendance surface (desktop calendar, mobile list, mobile week grid):
 * status overrides from the color legend take precedence, otherwise the session
 * falls back to the instructor's assigned color.
 *
 * Centralizing this keeps the color logic from drifting between views.
 */
export function getSessionBackgroundColor(
	session: FE_ScheduleSession,
	colorLegend: FeatFlagCalendarSessionColorLegend = DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
): string {
	const hasNoParticipants =
		!session.isCanceled && session.enrolledCustomers === 0;

	if (session.isCanceled) {
		return colorLegend[CalendarSessionColorStatus.CANCELED];
	}
	if (hasNoParticipants) {
		return colorLegend[CalendarSessionColorStatus.EMPTY];
	}
	if (session.hasTrialAttendance) {
		return colorLegend[CalendarSessionColorStatus.TRIAL];
	}
	if (session.isReposition) {
		return colorLegend[CalendarSessionColorStatus.REPOSITION];
	}
	if (session.hasExpiredEnrollment) {
		return colorLegend[CalendarSessionColorStatus.EXPIRED_ENROLLMENT];
	}
	return session.staffMemberColor || DEFAULT_SESSION_COLOR;
}

/**
 * Resolve the session title shown on the calendar, honoring the org's
 * `sessionTitleViewMode` feature flag. Shared by every attendance surface
 * (desktop calendar card, mobile list card, mobile week grid) so the three
 * views never drift on what a session is labeled with.
 *
 * CUSTOMER_NAMES falls back to the schedule label when a session has no enrolled
 * customers, so empty sessions still read sensibly instead of rendering blank.
 * `scheduleLabel` is passed in already-formatted because it comes from the
 * `useScheduleLabel` hook, which cannot run inside a plain util.
 */
export function getSessionDisplayTitle(
	session: FE_ScheduleSession,
	scheduleLabel: string,
	sessionTitleViewMode: FeatFlagCalendarSessionTitleViewMode = FeatFlagCalendarSessionTitleViewMode.SCHEDULE_NAME,
): string {
	switch (sessionTitleViewMode) {
		case FeatFlagCalendarSessionTitleViewMode.INSTRUCTOR_NAME:
			return session.staffMemberName;
		case FeatFlagCalendarSessionTitleViewMode.CUSTOMER_NAMES:
			return session.sessionCustomers && session.sessionCustomers.length > 0
				? session.sessionCustomers.map((customer) => customer.name).join(", ")
				: scheduleLabel;
		default:
			return scheduleLabel;
	}
}

/**
 * Convert a ScheduleSession from the backend to a FE_ScheduleSession for the frontend
 * @param session - The schedule session from the backend
 * @param enrolledCount - Number of enrolled customers
 * @returns FE_ScheduleSession formatted for FullCalendar and UI display
 */
export function convertScheduleSessionToEventSession(
	session: API_ScheduleSessionWithRelationsResponse,
	enrolledCount = 0,
): FE_ScheduleSession {
	// session.sessionDate is a CalendarDate ("YYYY-MM-DD"). Parse its parts
	// directly — never `new Date(calendarDate)`, which is UTC midnight and shifts
	// a day when rendered in a local zone. Build the FullCalendar instants at UTC
	// so 09:00 stays 09:00 in the UTC-rendered calendar.
	const [year, month, day] = session.sessionDate.split("-").map(Number);
	const [startHour, startMinute] = session.startTime.split(":").map(Number);
	const [endHour, endMinute] = session.endTime.split(":").map(Number);

	const startDateTime = new Date(
		Date.UTC(year, month - 1, day, startHour, startMinute, 0, 0),
	);
	const endDateTime = new Date(
		Date.UTC(year, month - 1, day, endHour, endMinute, 0, 0),
	);

	// Get staff member name from the staffMember relation
	let staffMemberName = "Unknown Staff";

	if (session.staffMember) {
		const firstName = session.staffMember.firstName || "";
		const lastName = session.staffMember.lastName || "";
		const fullName = `${firstName} ${lastName}`.trim();
		if (fullName) {
			staffMemberName = fullName;
		} else if (session.staffMember.email) {
			// Fallback to email if no name is available
			staffMemberName = session.staffMember.email;
		}
	}

	// Get establishment name from the establishment relation
	let establishmentName = "Unknown Establishment";
	if (session.establishment?.name) {
		establishmentName = session.establishment.name;
	}

	// Get staff member color from the staffMember relation (or undefined if not set)
	const staffMemberColor = session.staffMember?.color ?? undefined;

	return {
		id: session.id,
		scheduleId: session.scheduleId,
		organizationId: session.organizationId,
		establishmentId: session.establishmentId,
		staffMemberId: session.staffMemberId,
		description: session.description,
		type: session.type,
		capacity: session.capacity,
		startTime: startDateTime.toISOString(),
		endTime: endDateTime.toISOString(),
		sessionDate: session.sessionDate,
		isActive: session.isActive,
		isCanceled: session.isCanceled,
		notes: session.notes,
		// Structural inputs the UI resolves into a localized label via
		// `formatScheduleLabel`. A session is a concrete occurrence, so it renders
		// its date (SINGLE) rather than a weekly day list. `startTime` keeps the raw
		// HH:MM (not the ISO calendar value) so the label reads "09:00".
		labelInputs: {
			type: session.type,
			staffFirstName: session.staffMember?.firstName ?? "",
			startTime: session.startTime,
			recurrencyType: RecurrencyType.SINGLE,
			daysOfWeek: [],
			sessionDate: session.sessionDate,
		},
		staffMemberName,
		staffMemberColor,
		establishmentName,
		enrolledCustomers: enrolledCount,
		sessionCustomers: session.sessionCustomers,
		hasTrialAttendance: session.hasTrialAttendance,
		createdAt: session.createdAt,
		updatedAt: session.updatedAt,
	};
}

/**
 * Format session time for display in UTC
 * @param startTime - ISO string start time
 * @param endTime - ISO string end time
 * @returns Formatted time range string (e.g., "09:00 - 10:00")
 */
export function formatSessionTime(startTime: string, endTime: string): string {
	// Parse as UTC to avoid timezone shifts
	const start = new Date(startTime);
	const end = new Date(endTime);

	const formatTime = (date: Date) => {
		// Use UTC methods to format time consistently
		const hours = date.getUTCHours();
		const minutes = date.getUTCMinutes();

		// Format as HH:MM in 24-hour format to avoid timezone confusion
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
	};

	return `${formatTime(start)} - ${formatTime(end)}`;
}

/**
 * Extract the UTC wall-clock "HH:MM" from an ISO session instant, e.g. for
 * pre-populating `<input type="time">` fields.
 */
export function formatSessionClockTime(isoTime: string): string {
	const date = new Date(isoTime);
	const hours = date.getUTCHours().toString().padStart(2, "0");
	const minutes = date.getUTCMinutes().toString().padStart(2, "0");
	return `${hours}:${minutes}`;
}
